import { useEffect, useRef } from 'react';

import { useNarrowScreen } from '../view-mode.js';

/**
 * Карточка задачи на телефоне закрывается, если потянуть её вниз (3.5.7), —
 * как любая шторка в iOS, и рука это уже знает.
 *
 * Тянуть можно за ручку над названием всегда, а за свободное место карточки —
 * только когда она прокручена до самого верха: иначе движение вниз — это
 * прокрутка содержимого. Поля ввода жест не берут: там палец ставит курсор и
 * выделяет текст. Пока открыта клавиатура, жест выключен целиком.
 *
 * Сворачивать до половины высоты не стали (обсуждено с владельцем
 * 2026-10-17): список за карточкой закрыт подложкой и нажатий не принимает, а
 * в половине высоты карточку пришлось бы прокручивать.
 *
 * Карточка едет за пальцем без перерисовки React — стиль пишется прямо в
 * элемент: перерисовка всей карточки на каждый `touchmove` дёргала бы
 * движение. Слушатели родные: гасить прокрутку можно только непассивным
 * `touchmove`, а React вешает его пассивным.
 */

/** Сдвиг, после которого понятно, тянут ли вниз или листают. */
const LOCK_PX = 10;

/**
 * Дотянул — закрылась: четверть высоты, но не меньше 80 пикселей. Резкий
 * смах вниз закрывает и раньше — так ведёт себя шторка в самой iOS.
 */
export function closesAt(height: number): number {
  return Math.max(80, height * 0.25);
}

/** Скорость смаха, пикселей в миллисекунду. */
const FLICK_SPEED = 0.6;
const FLICK_MIN_PX = 40;

export const SHEET_MS = 200;

/** Где жест не начинается: там у пальца своя работа. */
const OWN_GESTURES = 'input, textarea, select, [contenteditable="true"], .menu__list';

export function useSheetDrag(options: { enabled: boolean; onClose: () => void }): {
  ref: React.RefObject<HTMLElement | null>;
} {
  const narrow = useNarrowScreen();
  const ref = useRef<HTMLElement | null>(null);
  const latest = useRef(options);
  latest.current = options;
  const enabled = narrow && options.enabled;

  useEffect(() => {
    const sheet = ref.current;
    if (!enabled || sheet === null) return undefined;
    const panel: HTMLElement = sheet;

    let gesture:
      | {
          y0: number;
          x0: number;
          /** Тянуть вниз можно: за ручку или тело и так у верха. */
          canPull: boolean;
          mode: 'pending' | 'drag' | 'none';
          lastY: number;
          lastT: number;
          speed: number;
        }
      | undefined;
    let closing = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const backdrop = (): HTMLElement | null =>
      panel.parentElement?.querySelector<HTMLElement>('.overlay__backdrop') ?? null;
    const body = (): HTMLElement | null => panel.querySelector<HTMLElement>('.panel__body');

    function place(offset: number, animate: boolean): void {
      const transition = animate ? `transform ${String(SHEET_MS)}ms ease-out` : 'none';
      panel.style.transition = transition;
      panel.style.transform = offset === 0 ? '' : `translate3d(0, ${String(offset)}px, 0)`;
      const shade = backdrop();
      if (shade !== null) {
        shade.style.transition = animate ? `opacity ${String(SHEET_MS)}ms ease-out` : 'none';
        shade.style.opacity =
          offset === 0 ? '' : String(Math.max(0, 1 - offset / panel.offsetHeight));
      }
    }

    function onStart(event: TouchEvent): void {
      const touch = event.touches[0];
      gesture = undefined;
      if (closing || event.touches.length !== 1 || touch === undefined) return;
      const target = event.target instanceof Element ? event.target : null;
      const fromGrip = target?.closest('.panel__grip') != null;
      if (!fromGrip && target?.closest(OWN_GESTURES) != null) return;

      gesture = {
        y0: touch.clientY,
        x0: touch.clientX,
        canPull: fromGrip || (body()?.scrollTop ?? 0) <= 0,
        mode: 'pending',
        lastY: touch.clientY,
        lastT: event.timeStamp,
        speed: 0,
      };
    }

    function onMove(event: TouchEvent): void {
      const touch = event.touches[0];
      if (gesture === undefined || gesture.mode === 'none' || touch === undefined) return;

      const dy = touch.clientY - gesture.y0;
      const dx = touch.clientX - gesture.x0;

      if (gesture.mode === 'pending') {
        // Первые пиксели вниз гасятся сразу, ещё до решения, жест ли это.
        // Иначе iOS успевает отдать их прокрутке страницы под карточкой, и
        // неделя за ней тянется «резинкой» (замечание владельца 2026-10-18);
        // начатую прокрутку поздний `preventDefault` уже не остановит.
        if (gesture.canPull && dy > 0 && dy >= Math.abs(dx)) event.preventDefault();

        if (Math.abs(dx) > LOCK_PX && Math.abs(dx) > Math.abs(dy)) {
          gesture.mode = 'none';
          return;
        }
        if (dy < -LOCK_PX) {
          gesture.mode = 'none';
          return;
        }
        if (dy <= LOCK_PX) return;
        // Вниз: шторка — если тянут за ручку или листать выше уже некуда.
        gesture.mode = gesture.canPull ? 'drag' : 'none';
        if (gesture.mode === 'none') return;
      }

      event.preventDefault();
      const elapsed = event.timeStamp - gesture.lastT;
      if (elapsed > 0) gesture.speed = (touch.clientY - gesture.lastY) / elapsed;
      gesture.lastY = touch.clientY;
      gesture.lastT = event.timeStamp;
      place(Math.max(0, dy), false);
    }

    function onEnd(): void {
      const ended = gesture;
      gesture = undefined;
      if (ended?.mode !== 'drag') return;

      const offset = Math.max(0, ended.lastY - ended.y0);
      const flick = ended.speed > FLICK_SPEED && offset > FLICK_MIN_PX;
      if (offset < closesAt(panel.offsetHeight) && !flick) {
        place(0, true);
        return;
      }

      closing = true;
      place(panel.offsetHeight + 24, true);
      timer = setTimeout(() => latest.current.onClose(), SHEET_MS);
    }

    function onCancel(): void {
      if (gesture?.mode === 'drag') place(0, true);
      gesture = undefined;
    }

    panel.addEventListener('touchstart', onStart, { passive: true });
    panel.addEventListener('touchmove', onMove, { passive: false });
    panel.addEventListener('touchend', onEnd);
    panel.addEventListener('touchcancel', onCancel);
    return () => {
      clearTimeout(timer);
      panel.removeEventListener('touchstart', onStart);
      panel.removeEventListener('touchmove', onMove);
      panel.removeEventListener('touchend', onEnd);
      panel.removeEventListener('touchcancel', onCancel);
      // Жест выключили посреди движения (открылась клавиатура) — карточка
      // не должна остаться сдвинутой.
      if (!closing) place(0, false);
    };
  }, [enabled]);

  return { ref };
}
